/**
 * <ScoreCard> — F044 §9.1. Shows the ONE composite Governance Score ONLY when `isScoreable`;
 * otherwise renders `<EmptyVerdict>`. Never renders "0" as a score. Links to the single decomposition
 * route (`/governance/score/decomposition`) — the ONE place the composite is decomposed.
 */
import type { FC } from 'react';
import { Body1, Card, CardHeader, Link, Title1, Title3, makeStyles, tokens } from '@fluentui/react-components';
import type { GovernanceScore, VerdictAction } from '../../types';
import { EmptyVerdict } from './EmptyVerdict';

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  composite: {
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
  },
  footer: {
    color: tokens.colorNeutralForeground3,
  },
});

export interface ScoreCardProps {
  score: GovernanceScore;
  decompositionRoute?: string;
  /** The next action shown inside `<EmptyVerdict>` when the score is not yet computable. */
  notScoreableAction: VerdictAction;
}

export const ScoreCard: FC<ScoreCardProps> = ({ score, decompositionRoute = '/governance/score/decomposition', notScoreableAction }) => {
  const styles = useStyles();

  if (!score.isScoreable || score.composite === undefined) {
    return (
      <EmptyVerdict
        verdict={{
          kind: 'State',
          label: 'Not Scoreable',
          colorIntent: 'Neutral',
          status: 'NOT SCOREABLE',
          why: 'The Governance Score requires an active tenant with a non-zero denominator (F043 guard).',
          impact: 'No composite score can be shown until governed data exists.',
          action: notScoreableAction,
        }}
      />
    );
  }

  return (
    <Card className={styles.card}>
      <CardHeader header={<Title3>Governance Score</Title3>} />
      <Title1 className={styles.composite}>{score.composite.toFixed(1)}</Title1>
      <Body1 className={styles.footer}>
        Weight profile <strong>{score.weightProfileId}</strong> — computed {new Date(score.computedAt).toLocaleString()}
      </Body1>
      <Link href={decompositionRoute}>View decomposition</Link>
    </Card>
  );
};
